import type { Dispatch, SetStateAction } from 'react';
import {
  computeTdsLines,
  formatBasisPoints,
  fromMinor,
  tdsBaseFor,
  totalTdsBase,
  type Invoice,
  type TdsLineInput,
  type TdsProfile,
  type TdsRateOption,
  type TdsSectionMaster,
} from '@fpc/shared';
import { rupeesToMinor } from '@/lib/format';

/**
 * TDS lines as they are keyed — on Invoice details at review and in the
 * accounting workbench. Each line picks a section and one of that section's
 * rates from Settings → TDS Sections, and the part of the taxable value it is
 * withheld on.
 */

/** One TDS line as typed: text, so a half-typed figure is not lost. */
export interface LineDraft {
  key: number;
  label: string;
  section: string;
  /** Basis points as text, '' while no rate is picked. */
  rate: string;
  /** In rupees (major units), as typed. */
  base: string;
}

let nextLineKey = 1;

export function newLine(profile: TdsProfile | undefined, baseAmount: number): LineDraft {
  return {
    key: nextLineKey++,
    label: profile?.label ?? '',
    section: profile?.section ?? '',
    rate: profile ? String(profile.rateBasisPoints) : '',
    base: baseAmount > 0 ? String(fromMinor(baseAmount)) : '',
  };
}

/**
 * The vendor profile a line came from: by name, or — for a deduction recorded
 * before lines had names — by section and rate.
 */
export function profileOf(profiles: TdsProfile[], line: LineDraft): TdsProfile | undefined {
  if (line.label) return profiles.find((profile) => profile.label === line.label);
  return profiles.find(
    (profile) => profile.section === line.section && String(profile.rateBasisPoints) === line.rate,
  );
}

/**
 * The lines with every vendor profile not already among them added, each
 * waiting for the amount it applies to. With no lines yet, the default
 * profile comes first, on the whole taxable value.
 */
export function withVendorProfiles(
  lines: LineDraft[],
  profiles: TdsProfile[],
  taxable: number,
): LineDraft[] {
  const next = lines.length
    ? [...lines]
    : [
        newLine(
          profiles.find((profile) => profile.isDefault),
          taxable,
        ),
      ];
  for (const profile of profiles) {
    if (next.some((line) => profileOf(profiles, line) === profile)) continue;
    next.push(newLine(profile, 0));
  }
  return next;
}

/** The invoice's lines as drafts, or its single deduction as one line. */
export function draftsOf(invoice: Invoice): LineDraft[] {
  if (invoice.tdsLines?.length) {
    return invoice.tdsLines.map((line) => ({
      key: nextLineKey++,
      label: line.label ?? '',
      section: line.section,
      rate: String(line.rateBasisPoints),
      base: String(fromMinor(line.baseAmount)),
    }));
  }
  if (!invoice.tdsApplicable) return [];
  return [
    {
      key: nextLineKey++,
      label: '',
      section: invoice.tdsSection ?? '',
      rate: invoice.tdsRateBasisPoints ? String(invoice.tdsRateBasisPoints) : '',
      base: String(fromMinor(invoice.tdsBaseAmount ?? tdsBaseFor(invoice))),
    },
  ];
}

/** The drafts as the server takes them; anything not yet a number counts as 0. */
export function parseLines(lines: LineDraft[]): TdsLineInput[] {
  return lines.map((line) => ({
    ...(line.label ? { label: line.label } : {}),
    section: line.section,
    rateBasisPoints: Number(line.rate || 0),
    baseAmount: rupeesToMinor(line.base) ?? 0,
  }));
}

/**
 * The rates a line may pick: its section's, plus the rate it already carries
 * when that is not among them (read off the invoice), so nothing is lost.
 */
export function rateOptions(ratesFor: (code: string) => TdsRateOption[], line: LineDraft) {
  const rates = ratesFor(line.section);
  const current = Number(line.rate);
  if (current && !rates.some((rate) => rate.rateBasisPoints === current)) {
    return [...rates, { label: 'as recorded', rateBasisPoints: current }];
  }
  return rates;
}

export function TdsLinesEditor({
  lines,
  setLines,
  profiles,
  taxable,
  sections,
  ratesFor,
  successorOf,
  format,
}: {
  lines: LineDraft[];
  setLines: Dispatch<SetStateAction<LineDraft[]>>;
  profiles: TdsProfile[];
  /** The invoice's taxable value, which the lines together may not exceed. */
  taxable: number;
  sections: TdsSectionMaster[];
  ratesFor: (code: string) => TdsRateOption[];
  /** The code now in force for one since replaced, e.g. 194IC for 194IB. */
  successorOf?: (code: string) => string | undefined;
  format(amount: number): string;
}) {
  const updateLine = (key: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  const addLine = () =>
    setLines((current) => {
      const used = new Set(current.map((line) => line.label));
      const next = profiles.find((profile) => !used.has(profile.label));
      const remaining = Math.max(0, taxable - totalTdsBase(parseLines(current)));
      return [...current, newLine(next, remaining)];
    });
  // A new section offers its own rates: keep the rate if it is one, take the
  // only one if there is just one, else leave it to be picked.
  const pickSection = (line: LineDraft, section: string) => {
    const rates = ratesFor(section);
    const keep = rates.some((rate) => String(rate.rateBasisPoints) === line.rate);
    updateLine(line.key, {
      section,
      rate: keep ? line.rate : rates.length === 1 ? String(rates[0]!.rateBasisPoints) : '',
    });
  };
  const baseTotal = totalTdsBase(parseLines(lines));
  // A section on the invoice that is no longer in the list stays selectable,
  // saying which code has replaced it when one has.
  const sectionOptions = (line: LineDraft) => {
    if (!line.section || sections.some((section) => section.code === line.section)) {
      return sections;
    }
    const now = successorOf?.(line.section);
    const name = now ? `as recorded · now ${now}` : 'as recorded';
    return [...sections, { code: line.section, name } as TdsSectionMaster];
  };

  return (
    <div className="space-y-2" role="group" aria-label="TDS lines">
      {/*
        One line per kind of service: fees under 194J and maintenance
        under 194C each withhold on their own part of the taxable value.
      */}
      {lines.map((line, index) => {
        const computed = computeTdsLines(parseLines([line]))[0];
        return (
          // Two fields to a row: both screens show this beside the document, in a
          // column too narrow for section and rate names side by side.
          <div
            key={line.key}
            className="grid grid-cols-2 items-end gap-2 rounded-md border border-slate-200 p-2"
          >
            <label className="block">
              <span className="label">For</span>
              <select
                className="input"
                aria-label={`TDS line ${index + 1} profile`}
                value={profileOf(profiles, line)?.label ?? ''}
                onChange={(event) => {
                  const profile = profiles.find((entry) => entry.label === event.target.value);
                  updateLine(
                    line.key,
                    profile
                      ? {
                          label: profile.label,
                          section: profile.section,
                          rate: String(profile.rateBasisPoints),
                        }
                      : { label: '' },
                  );
                }}
              >
                <option value="">Other</option>
                {profiles.map((profile) => (
                  <option key={profile.label} value={profile.label}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">Section</span>
              <select
                className="input"
                aria-label={`TDS line ${index + 1} section`}
                value={line.section}
                onChange={(event) => pickSection(line, event.target.value)}
              >
                <option value="">Choose…</option>
                {sectionOptions(line).map((section) => (
                  <option key={section.code} value={section.code}>
                    {section.code} · {section.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">Rate</span>
              <select
                className="input"
                aria-label={`TDS line ${index + 1} rate`}
                value={line.rate}
                disabled={!line.section}
                onChange={(event) => updateLine(line.key, { rate: event.target.value })}
              >
                <option value="">{line.section ? 'Choose…' : 'Pick a section'}</option>
                {rateOptions(ratesFor, line).map((rate) => (
                  <option key={rate.rateBasisPoints} value={String(rate.rateBasisPoints)}>
                    {formatBasisPoints(rate.rateBasisPoints)} · {rate.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">On amount</span>
              <input
                className="input tabular"
                aria-label={`TDS line ${index + 1} amount it applies to`}
                inputMode="decimal"
                value={line.base}
                onChange={(event) => updateLine(line.key, { base: event.target.value })}
              />
            </label>
            <div className="col-span-2 flex items-center justify-between gap-3">
              {line.base.trim() && line.rate ? (
                <span className="text-sm font-semibold text-amber-700">
                  {format(computed?.amount ?? 0)}
                </span>
              ) : (
                <span className="text-xs text-slate-500">Not applied</span>
              )}
              <button
                type="button"
                className="block text-xs text-red-600 sm:ml-auto"
                onClick={() =>
                  setLines((current) => current.filter((entry) => entry.key !== line.key))
                }
              >
                Remove
              </button>
            </div>
          </div>
        );
      })}

      <button type="button" className="btn-secondary" onClick={addLine}>
        Add TDS line
      </button>

      {baseTotal > taxable ? (
        <p className="notice-warning text-xs">
          The lines are on {format(baseTotal)}, more than the taxable value of {format(taxable)}.
        </p>
      ) : null}
    </div>
  );
}
